import AsyncStorage from '@react-native-async-storage/async-storage';
import { PokusLobs } from '@mmt/legacy-commons/Native/AbConfig/AbConfigKeyMappings';
import { trackOmniture, trackOmnitureOverrideList2 } from '@mmt/legacy-commons/Common/utils/OmnitureTrackerUtils';

import {
  AbConfigKeyMappings,
  getPokusConfig
} from '@mmt/legacy-commons/Native/AbConfig/AbConfigModule';
import { TrainBtwnStnsCP, TbsAvailabilityCP } from '../types/confirmationProbability.types';
import { NewListingTrain, NewListingTBSAval, AvailableClass, Quota } from '../types/newListing.types';
import { isMweb } from "./device";
import RAIL_EVENTS from 'apps/rails/src/RailsOmnitureTracker';
// ===============================================================================

export const fetchConfirmationGuaranteeOption = async () => {
  const railsConfirmationGuaranteeOption = await getPokusConfig(
    PokusLobs.RAIL,
    AbConfigKeyMappings.railsConfirmationGuaranteeOption,
    0
  ); // 0, 1, 2 etc
  return railsConfirmationGuaranteeOption;
}

export const fetchFreeCancellationOption = async () => {
  const freeCancellationEnabled = await getPokusConfig(
    PokusLobs.RAIL,
    AbConfigKeyMappings.freeCancellationRails,
    false
  ); // true false
  await saveFreeCancellationPokusConfig(freeCancellationEnabled);
  return freeCancellationEnabled;
}

const FREE_CANCELLATION_POKUS_LS_KEY = 'COM.MAKEMYTRIP.RAIL.TRAVELLER.FREE.CANCELLATION.POKUS';

const saveFreeCancellationPokusConfig = async (showFreeCancellation: boolean) => {
  await AsyncStorage.setItem(FREE_CANCELLATION_POKUS_LS_KEY, JSON.stringify(showFreeCancellation));
};

export const getFreeCancellationPokusConfig = async () => {
  const config = await AsyncStorage.getItem(FREE_CANCELLATION_POKUS_LS_KEY);
  return JSON.parse(config || "false");
};

const RAILOFY_ZC_SHOWN_LS_KEY = 'COM.MAKEMYTRIP.RAIL.TRAVELLER.RAILOFY.ZC.SHOWN';
interface RailofyZcShownOptions {
  railofyShown: boolean;
  zcShown: boolean;
};

export const saveRailofyZcShownInLS = async(data: RailofyZcShownOptions) => {
  await AsyncStorage.setItem(RAILOFY_ZC_SHOWN_LS_KEY, JSON.stringify(data));
};

export const getRailofyZcShownInLS = async () => {
  const data = await AsyncStorage.getItem(RAILOFY_ZC_SHOWN_LS_KEY);
  return JSON.parse(data) || { railofyShown: false, zcShown: false };
};

export const trackRailofyKnowMoreClick = (pageName: string) => {
  const params = {
    pageName,
    m_v15: pageName,
    m_c61: '3XRefundKnowMoreClicked',
    m_v24: 'rails'
  };
  trackOmniture(pageName, params);
};

interface TrackShownOptedInterface {
  fcShown: boolean | string;
  fcOpted: boolean | string;
  railofyShown: boolean | string;
  railofyOpted: boolean | string;
  pageName: string;
  tgpShown: boolean | string;
  tgpOpted: boolean | string;
}
export const trackShownAndOptedValues = (values: TrackShownOptedInterface) => {
  const {
    fcShown,
    fcOpted,
    railofyShown,
    railofyOpted,
    pageName,
    tgpShown,
    tgpOpted,
  } = values;

  const fcTrackingValues = `FCShown${fcShown}|FCOpted${fcOpted}|`;
  const tgTrackingValues = `3XRefundShown${railofyShown}|3XRefundOpted${railofyOpted}|`;
  const tgPlusTrackingValues = `TGPlusShown${tgpShown}|TGPlusOpted${tgpOpted}|`;

  const trackingParams = [fcTrackingValues, tgTrackingValues, tgPlusTrackingValues];

  const trackingValue = trackingParams?.reduce((accumulator, currentTrackingParam) => {
    return accumulator + currentTrackingParam;
  }, '');

  const params = {
    pageName,
    m_v15: pageName,
    m_v24: 'rails',
    m_c1: trackingValue,
  };
  trackOmniture(pageName, params);
};

type AvlByQuotaByClass = {
  [quotaName in Quota]: {
    [className in AvailableClass]?: NewListingTBSAval
  }
}

interface TrainObject {
  [trainNumber: string]: {
    tbsAvlByQuotaByClass: AvlByQuotaByClass
  }
}

interface CountObj {
  totalTicket: number;
  wlTicket: number;
  fcStamp: number;
  railofyCount: number;
};

export const formatTrackingVal = (train: TrainBtwnStnsCP, trainObject: TrainObject, countObj: CountObj) => {
  const getAvailabilityStatus = (tbsAvl: TbsAvailabilityCP) => {
    const tbsAvlByQuotaByClass = trainObject[train.trainNumber]?.tbsAvlByQuotaByClass?.[tbsAvl.quota]?.[tbsAvl.className];
    const prettyPrintingAvailablityStatus: string = tbsAvlByQuotaByClass?.prettyPrintingAvailablityStatus || 'Tap To Update';
    const trackingStatusString = prettyPrintingAvailablityStatus.split(' ').filter(str => isNaN(Number(str))).join('');
    countObj.totalTicket += 1;
    if (tbsAvlByQuotaByClass?.availablityType === "3") {
      countObj.wlTicket += 1;
    }
    if (tbsAvl.confirmationGuaranteeText) {
      countObj.railofyCount += 1;
    }
    if (tbsAvl.freeCancellationText) {
      countObj.fcStamp += 1;
    }
    // console.log('trackingStatusString', prettyPrintingAvailablityStatus, tbsAvlByQuotaByClass?.wlType);
    return trackingStatusString;
  };
  const separator = isMweb() ? '|' : '!';
  const value = train?.tbsAvailability?.reduce((acc, tbsAvl) => `${acc}trains3x::${train.trainNumber}:${tbsAvl.className}:${tbsAvl.quota}:${getAvailabilityStatus(tbsAvl)}:${tbsAvl.predictionPercentage}${separator}`, '');
  return value;
};

export const trackTotalCardsOnListing = (trainBtwnStnsList: TrainBtwnStnsCP[], trainsList: NewListingTrain[]) => {
  const trainObject = trainsList?.reduce((acc: TrainObject, train) => {
    acc[train.trainNumber] = {
      tbsAvlByQuotaByClass: train?.tbsAvailability?.reduce((accQC, tbsAvl) => {
        const { quota, className } = tbsAvl;
        if (accQC[quota]) {
          accQC[quota][className] = tbsAvl;
        } else {
          accQC[quota] = {
            [className]: tbsAvl,
          };
        }
        return accQC;
      }, {} as AvlByQuotaByClass),
    };
    return acc;
  }, {} as TrainObject);

  const countObj = {
    totalTicket: 0,
    wlTicket: 0,
    fcStamp: 0,
    railofyCount: 0
  };
  const trackingData = trainBtwnStnsList?.reduce((acc, train) => `${acc}${formatTrackingVal(train, trainObject, countObj)}`, '');
  const pageName = 'mob_rail_listing_v2';
  const params: any = {
    pageName,
    m_v24: 'rails',
  };
  const countParams: any = {};

  if (isMweb()) {
    params.m_list3 = trackingData;
    countParams.events = `event306=${countObj.totalTicket},event307=${countObj.wlTicket},event308=${countObj.fcStamp},event309=${countObj.railofyCount}`;

    trackOmniture(pageName, countParams);
    trackOmniture(pageName, params);
  } else {
    params.m_list2 = trackingData;
    countParams.events = `event406=${countObj.totalTicket},event407=${countObj.wlTicket},event408=${countObj.fcStamp},event409=${countObj.railofyCount}`;

    trackOmnitureOverrideList2(pageName, countParams);
    trackOmniture(pageName, params);
  }
};

export const updatePreserveFcSelection = (index) => {
  if(index != null) {
    return {
      preserveFcSelection: index === 0,
    }
  }
  return {}
}
